<template>
  <div class="relative" ref="containerRef">
    <!-- Canvas for gradient -->
    <canvas
      ref="canvasRef"
      :width="size"
      :height="size"
      class="rounded-full relative z-10"
      @mousedown="handleCanvasClick"
      @mousemove="handleCanvasMove"
      @mouseup="handleCanvasRelease"
      @mouseleave="handleCanvasRelease"
    />
    
    <!-- Art Deco Frame - Above canvas (optional) -->
    <svg 
      v-if="showFrame"
      viewBox="0 0 200 200" 
      class="art-deco-frame"
      :style="{ 
        width: `${size + 80}px`, 
        height: `${size + 80}px`,
        left: '-40px',
        top: '-40px',
        zIndex: '15'
      }"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id="circleGoldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="var(--gold-start)" />
          <stop offset="50%" stop-color="var(--gold-mid)" />
          <stop offset="100%" stop-color="var(--gold-end)" />
        </linearGradient>
        <filter id="circleGlow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <!-- Full golden ring around circle -->
      <circle 
        cx="100" 
        cy="100" 
        r="76" 
        fill="none"
        stroke="url(#circleGoldGradient)"
        stroke-width="2"
        filter="url(#circleGlow)"
        opacity="0.7"
      />

      <!-- Top ornament -->
      <g transform="translate(100, 20)">
        <path
          d="M 0 0 L 5 8 L 0 10 L -5 8 Z"
          fill="none"
          stroke="url(#circleGoldGradient)"
          stroke-width="2"
          filter="url(#circleGlow)"
        />
        <path
          d="M 0 3 L 3 7 L 0 8 L -3 7 Z"
          fill="url(#circleGoldGradient)"
          opacity="0.4"
        />
      </g>

      <!-- Corner brackets - Top Left -->
      <g transform="translate(32, 32)">
        <!-- Main bracket sharp angles -->
        <path
          d="M 0 25 L 0 5 L 5 0 L 25 0"
          fill="none"
          stroke="url(#circleGoldGradient)"
          stroke-width="3"
          filter="url(#circleGlow)"
          stroke-linejoin="miter"
        />
        <!-- Inner accent line -->
        <path
          d="M 5 20 L 5 10 L 10 5 L 20 5"
          stroke="url(#circleGoldGradient)"
          stroke-width="2"
          opacity="0.6"
          fill="none"
          stroke-linejoin="miter"
        />
        <!-- Outer decorative extensions -->
        <path
          d="M 28 0 L 35 0 M 0 28 L 0 35"
          stroke="url(#circleGoldGradient)"
          stroke-width="2.5"
          filter="url(#circleGlow)"
          opacity="0.8"
        />
        <!-- Corner detail -->
        <circle cx="5" cy="0" r="1.5" fill="url(#circleGoldGradient)" filter="url(#circleGlow)" />
        <circle cx="0" cy="5" r="1.5" fill="url(#circleGoldGradient)" filter="url(#circleGlow)" />
      </g>

      <!-- Corner brackets - Top Right -->
      <g transform="translate(168, 32)">
        <!-- Main bracket sharp angles -->
        <path
          d="M 0 25 L 0 5 L -5 0 L -25 0"
          fill="none"
          stroke="url(#circleGoldGradient)"
          stroke-width="3"
          filter="url(#circleGlow)"
          stroke-linejoin="miter"
        />
        <!-- Inner accent line -->
        <path
          d="M -5 20 L -5 10 L -10 5 L -20 5"
          stroke="url(#circleGoldGradient)"
          stroke-width="2"
          opacity="0.6"
          fill="none"
          stroke-linejoin="miter"
        />
        <!-- Outer decorative extensions -->
        <path
          d="M -28 0 L -35 0 M 0 28 L 0 35"
          stroke="url(#circleGoldGradient)"
          stroke-width="2.5"
          filter="url(#circleGlow)"
          opacity="0.8"
        />
        <!-- Corner detail -->
        <circle cx="-5" cy="0" r="1.5" fill="url(#circleGoldGradient)" filter="url(#circleGlow)" />
        <circle cx="0" cy="5" r="1.5" fill="url(#circleGoldGradient)" filter="url(#circleGlow)" />
      </g>

      <!-- Corner brackets - Bottom Left -->
      <g transform="translate(32, 168)">
        <!-- Main bracket sharp angles -->
        <path
          d="M 0 -25 L 0 -5 L 5 0 L 25 0"
          fill="none"
          stroke="url(#circleGoldGradient)"
          stroke-width="3"
          filter="url(#circleGlow)"
          stroke-linejoin="miter"
        />
        <!-- Inner accent line -->
        <path
          d="M 5 -20 L 5 -10 L 10 -5 L 20 -5"
          stroke="url(#circleGoldGradient)"
          stroke-width="2"
          opacity="0.6"
          fill="none"
          stroke-linejoin="miter"
        />
        <!-- Outer decorative extensions -->
        <path
          d="M 28 0 L 35 0 M 0 -28 L 0 -35"
          stroke="url(#circleGoldGradient)"
          stroke-width="2.5"
          filter="url(#circleGlow)"
          opacity="0.8"
        />
        <!-- Corner detail -->
        <circle cx="5" cy="0" r="1.5" fill="url(#circleGoldGradient)" filter="url(#circleGlow)" />
        <circle cx="0" cy="-5" r="1.5" fill="url(#circleGoldGradient)" filter="url(#circleGlow)" />
      </g>

      <!-- Corner brackets - Bottom Right -->
      <g transform="translate(168, 168)">
        <!-- Main bracket sharp angles -->
        <path
          d="M 0 -25 L 0 -5 L -5 0 L -25 0"
          fill="none"
          stroke="url(#circleGoldGradient)"
          stroke-width="3"
          filter="url(#circleGlow)"
          stroke-linejoin="miter"
        />
        <!-- Inner accent line -->
        <path
          d="M -5 -20 L -5 -10 L -10 -5 L -20 -5"
          stroke="url(#circleGoldGradient)"
          stroke-width="2"
          opacity="0.6"
          fill="none"
          stroke-linejoin="miter"
        />
        <!-- Outer decorative extensions -->
        <path
          d="M -28 0 L -35 0 M 0 -28 L 0 -35"
          stroke="url(#circleGoldGradient)"
          stroke-width="2.5"
          filter="url(#circleGlow)"
          opacity="0.8"
        />
        <!-- Corner detail -->
        <circle cx="-5" cy="0" r="1.5" fill="url(#circleGoldGradient)" filter="url(#circleGlow)" />
        <circle cx="0" cy="-5" r="1.5" fill="url(#circleGoldGradient)" filter="url(#circleGlow)" />
      </g>

      <!-- Tall parenthesis-style brackets - Left -->
      <g transform="translate(28, 100)">
        <path
          d="M 0 -45 Q -12 -35 -12 0 Q -12 35 0 45"
          fill="none"
          stroke="url(#circleGoldGradient)"
          stroke-width="2.5"
          filter="url(#circleGlow)"
        />
        <circle cx="-12" cy="0" r="3" fill="url(#circleGoldGradient)" opacity="0.6" filter="url(#circleGlow)" />
      </g>

      <!-- Tall parenthesis-style brackets - Right -->
      <g transform="translate(172, 100)">
        <path
          d="M 0 -45 Q 12 -35 12 0 Q 12 35 0 45"
          fill="none"
          stroke="url(#circleGoldGradient)"
          stroke-width="2.5"
          filter="url(#circleGlow)"
        />
        <circle cx="12" cy="0" r="3" fill="url(#circleGoldGradient)" opacity="0.6" filter="url(#circleGlow)" />
      </g>

      <!-- Cardinal point decorations -->
      <g transform="translate(100, 100)" opacity="0.8">
        <!-- Top -->
        <path d="M -3 -76 L 0 -79 L 3 -76" fill="none" stroke="url(#circleGoldGradient)" stroke-width="2" filter="url(#circleGlow)" />
        <circle cx="0" cy="-77" r="2.5" fill="url(#circleGoldGradient)" filter="url(#circleGlow)" />
        
        <!-- Right -->
        <path d="M 76 -3 L 79 0 L 76 3" fill="none" stroke="url(#circleGoldGradient)" stroke-width="2" filter="url(#circleGlow)" />
        <circle cx="77" cy="0" r="2.5" fill="url(#circleGoldGradient)" filter="url(#circleGlow)" />
        
        <!-- Bottom -->
        <path d="M -3 76 L 0 79 L 3 76" fill="none" stroke="url(#circleGoldGradient)" stroke-width="2" filter="url(#circleGlow)" />
        <circle cx="0" cy="77" r="2.5" fill="url(#circleGoldGradient)" filter="url(#circleGlow)" />
        
        <!-- Left -->
        <path d="M -76 -3 L -79 0 L -76 3" fill="none" stroke="url(#circleGoldGradient)" stroke-width="2" filter="url(#circleGlow)" />
        <circle cx="-77" cy="0" r="2.5" fill="url(#circleGoldGradient)" filter="url(#circleGlow)" />
      </g>

      <!-- Diagonal accent marks -->
      <g transform="translate(100, 100)" opacity="0.6">
        <line x1="-55" y1="-55" x2="-60" y2="-60" stroke="url(#circleGoldGradient)" stroke-width="2" filter="url(#circleGlow)" />
        <line x1="55" y1="-55" x2="60" y2="-60" stroke="url(#circleGoldGradient)" stroke-width="2" filter="url(#circleGlow)" />
        <line x1="-55" y1="55" x2="-60" y2="60" stroke="url(#circleGoldGradient)" stroke-width="2" filter="url(#circleGlow)" />
        <line x1="55" y1="55" x2="60" y2="60" stroke="url(#circleGoldGradient)" stroke-width="2" filter="url(#circleGlow)" />
      </g>
    </svg>
    
    <!-- Light pips overlay -->
    <div class="absolute inset-0 pointer-events-none z-20">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { hsvToRGB, ctToRGB, type XYCoord } from '@/lib/colorConversion'

interface Props {
  type: 'hue' | 'white'
  size?: number
  disabled?: boolean
  showFrame?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 300,
  disabled: false,
  showFrame: false
})

const emit = defineEmits<{
  positionChange: [coord: XYCoord]
}>()

const canvasRef = ref<HTMLCanvasElement>()
const containerRef = ref<HTMLDivElement>()
const isDragging = ref(false)

// Cache canvas context and track last drawn config to prevent unnecessary redraws
const cachedContext = ref<CanvasRenderingContext2D | null>(null)
const lastDrawnConfig = ref<{ type: 'hue' | 'white'; size: number } | null>(null)

onMounted(() => {
  drawGradient()
})

watch(() => [props.type, props.size] as const, () => {
  // Only redraw if type or size changed
  const currentConfig = { type: props.type, size: props.size }
  if (!lastDrawnConfig.value || 
      lastDrawnConfig.value.type !== currentConfig.type ||
      lastDrawnConfig.value.size !== currentConfig.size) {
    drawGradient()
  }
})

const drawGradient = () => {
  const canvas = canvasRef.value
  if (!canvas) return
  
  const currentConfig = { type: props.type, size: props.size }
  
  // Skip if already drawn for this exact config
  if (lastDrawnConfig.value && 
      lastDrawnConfig.value.type === currentConfig.type &&
      lastDrawnConfig.value.size === currentConfig.size && 
      cachedContext.value) {
    return
  }
  
  // Get or reuse cached context
  const ctx = cachedContext.value || canvas.getContext('2d')
  if (!ctx) return
  
  cachedContext.value = ctx
  lastDrawnConfig.value = currentConfig
  
  const centerX = props.size / 2
  const centerY = props.size / 2
  const radius = props.size / 2
  
  // Clear canvas
  ctx.clearRect(0, 0, props.size, props.size)
  
  if (props.type === 'hue') {
    drawHueCircle(ctx, centerX, centerY, radius)
  } else {
    drawWhiteCircle(ctx, centerX, centerY, radius)
  }
}

const drawHueCircle = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number) => {
  // Draw color wheel with saturation gradient
  const imageData = ctx.createImageData(props.size, props.size)
  const data = imageData.data
  
  for (let y = 0; y < props.size; y++) {
    for (let x = 0; x < props.size; x++) {
      const dx = x - centerX
      const dy = y - centerY
      const distance = Math.sqrt(dx * dx + dy * dy)
      
      if (distance <= radius) {
        const angle = Math.atan2(dy, dx)
        const hue = ((angle * 180 / Math.PI) + 360) % 360
        const saturation = (distance / radius) * 100
        
        const rgb = hsvToRGB({ h: hue, s: saturation, v: 100 })
        
        const index = (y * props.size + x) * 4
        data[index] = rgb.r
        data[index + 1] = rgb.g
        data[index + 2] = rgb.b
        data[index + 3] = 255
      }
    }
  }
  
  ctx.putImageData(imageData, 0, 0)
  
  // Draw border
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
  ctx.stroke()
}

const drawWhiteCircle = (ctx: CanvasRenderingContext2D, centerX: number, centerY: number, radius: number) => {
  // Draw horizontal gradient from cool (left) to warm (right)
  const gradient = ctx.createLinearGradient(centerX - radius, centerY, centerX + radius, centerY)
  
  // Cool white (6500K / 153 mireds)
  const coolRGB = ctToRGB(153)
  gradient.addColorStop(0, `rgb(${coolRGB.r}, ${coolRGB.g}, ${coolRGB.b})`)
  
  // Neutral white (4000K / 250 mireds)
  const neutralRGB = ctToRGB(250)
  gradient.addColorStop(0.5, `rgb(${neutralRGB.r}, ${neutralRGB.g}, ${neutralRGB.b})`)
  
  // Warm white (2000K / 500 mireds)
  const warmRGB = ctToRGB(500)
  gradient.addColorStop(1, `rgb(${warmRGB.r}, ${warmRGB.g}, ${warmRGB.b})`)
  
  // Draw circle with gradient
  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
  ctx.fill()
  
  // Draw border
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2)
  ctx.stroke()
}

const getCircleCoords = (event: MouseEvent): XYCoord | null => {
  const canvas = canvasRef.value
  if (!canvas) return null
  
  const rect = canvas.getBoundingClientRect()
  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2
  const radius = rect.width / 2
  
  const dx = event.clientX - centerX
  const dy = event.clientY - centerY
  
  return {
    x: dx / radius,
    y: dy / radius
  }
}

const handleCanvasClick = (event: MouseEvent) => {
  if (props.disabled) return
  
  const coords = getCircleCoords(event)
  if (!coords) return
  
  // Check if click is within circle
  const distance = Math.sqrt(coords.x * coords.x + coords.y * coords.y)
  if (distance > 1) return
  
  isDragging.value = true
  emit('positionChange', coords)
}

const handleCanvasMove = (event: MouseEvent) => {
  if (props.disabled || !isDragging.value) return
  
  const coords = getCircleCoords(event)
  if (!coords) return
  
  emit('positionChange', coords)
}

const handleCanvasRelease = () => {
  isDragging.value = false
}

// Expose method for external position changes
const getCoords = (event: MouseEvent) => getCircleCoords(event)

defineExpose({
  getCoords
})
</script>

<style scoped>
canvas {
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
}

canvas.disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
